import { useFlag } from '@/components/posthog-provider';
import { Button } from '@/components/ui/button';
import { useListAssets } from '@/features/assets/api/list-assets';
import { useSelectedAssetMetadata } from '@/features/assets/api/use-selected-asset-metadata';
import {
  musicUploadAsTrack,
  useListMusicUploads,
} from '@/features/music/api/music-uploads.hook';
import {
  MusicEditorDialog,
  UPLOADED_TRACK_ID,
} from '@/features/music/components/music-editor-dialog';
import { useGetVideo } from '@/features/videos/api';
import { useListMusicTracks } from '@/features/videos/api/list-music-tracks';
import {
  type PatchVideoDraftConfigResponse,
  usePatchVideoDraftConfig,
} from '@/features/videos/api/patch-video-draft-config';
import { useQueueVideoExport } from '@/features/videos/api/queue-video-export';
import { usePrepareVideoPreview } from '@/features/videos/api/use-prepare-video-preview';
import { VideoThumbnail } from '@/routes/_authed/create-video/$templateId/-components/shared/video-thumbnail';
import { videoStatusLabels } from '@borradh-workspace/api-client/types';
import { STOCK_FOOTAGE_MATCHING_NEW_FLAG } from '@borradh-workspace/labels';
import { AUTHORED_TEMPLATES } from '@borradh-workspace/video-templates';
import { Music, Pencil, Sparkles, VideoIcon, X } from 'lucide-react';
import { Suspense, lazy, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { ComposerDecision } from '../composer-gate-context';
import { ClipPickerDialog } from './clip-picker-dialog';
import { DecisionFooter } from './decision-footer';
import { ProcessingStatus } from './processing-status';
import { draftCopyOf } from './video-draft-copy-model';
import type { EditableTextFrame } from './video-draft-edit-dialog';
import {
  getOrderedDraftClipAssetIds,
  isDraftVideoEditable,
} from './video-draft-selection';
import type { DraftEditorChange } from './video-draft-timeline-editor';

const VideoDraftEditDialog = lazy(() =>
  import('./video-draft-edit-dialog').then((m) => ({
    default: m.VideoDraftEditDialog,
  }))
);

export interface CreatedField {
  label: string;
  value: string;
}

type TextFrameStyle = 'default' | 'question' | 'answer' | 'disclaimer' | 'cta';

interface TextFramePreview {
  id: string;
  text: string;
  style?: TextFrameStyle;
}

interface VideoDraftPreviewProps {
  /** The tool call this card renders from — a stable id for gate registration. */
  id: string;
  videoId: string;
  title: string;
  fields: CreatedField[];
  serviceId?: string | null;
  initialClipAssetIds: string[];
  minClipCount?: number;
  textFrames?: TextFramePreview[];
  /**
   * Cards answer by SENDING A MESSAGE — there is no tool-output channel back
   * from a card. "Reject" has no server-side concept of a declined video
   * draft to write, so it says so in chat, same as `AssetReferenceCard` /
   * `DraftReplyCard` already do for their own decisions.
   */
  onSendMessage?: (message: string) => void;
  editorOnly?: boolean;
  onSaved?: (result: PatchVideoDraftConfigResponse) => void;
}

const STYLE_BADGES: Record<
  TextFrameStyle,
  { label: string; className: string }
> = {
  question: {
    label: 'Hook',
    className:
      'bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-200',
  },
  answer: {
    label: 'Body',
    className: 'bg-muted text-muted-foreground',
  },
  cta: {
    label: 'CTA',
    className:
      'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-200',
  },
  disclaimer: {
    label: 'Disclaimer',
    className:
      'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-200',
  },
  default: {
    label: 'Text',
    className: 'bg-muted text-muted-foreground',
  },
};

/**
 * Rich card the chat falls back to when a draft has no content item behind
 * it — see the `video_draft` envelope in `tool-renderer`.
 *
 * Confirmation-sentence shape, matching `GraphicDraftPreviewCard`: a
 * one-line pitch, a read-only clip strip, "Edit" opening the real
 * `ClipPickerDialog` (search/tags/stock/min-count), and Reject/Create video
 * in the footer. "Create video" calls the same `useQueueVideoExport`
 * mutation the other video-creation dialogs already use — approving a draft
 * used to be entirely conversational (the owner had to type something like
 * "go ahead" for the model to call `content_renderVideo`); this gives the
 * card its own direct trigger for the common case, same as graphics already
 * have via Accept.
 */
export function VideoDraftPreviewCard({
  id,
  videoId,
  title,
  fields,
  serviceId,
  initialClipAssetIds,
  minClipCount = 1,
  textFrames,
  onSendMessage,
  editorOnly = false,
  onSaved,
}: VideoDraftPreviewProps) {
  // The video this card is CURRENTLY about. Starts as the one the tool named
  // and moves to the fork when an edit to a rendered video creates one —
  // otherwise the owner picks a clip, a new video renders, and the card sits
  // watching the old one forever, showing the footage they just replaced.
  const [activeVideoId, setActiveVideoId] = useState(videoId);
  const [selectedIds, setSelectedIds] = useState<string[]>(initialClipAssetIds);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [pendingChange, setPendingChange] = useState<DraftEditorChange | null>(
    null
  );
  const pendingChangeRef = useRef<DraftEditorChange | null>(null);
  const [editorKey, setEditorKey] = useState(0);
  const [editableFrames, setEditableFrames] = useState<EditableTextFrame[]>(
    (textFrames ?? []).map((f) => ({ ...f, durationSec: 3 }))
  );
  const exportStarted = useRef(false);
  const [startingExport, setStartingExport] = useState(false);
  const [decision, setDecision] = useState<'pending' | 'rejected'>('pending');
  const [queuedVideo, setQueuedVideo] = useState<{
    videoId: string;
    status: string;
  } | null>(null);

  // Tool output is an immutable snapshot of the moment the draft was created.
  // Read the live video so a later render locks the picker and saved clip
  // changes survive conversation reloads.
  const { video } = useGetVideo(activeVideoId, {
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'ready' || status === 'failed' ? false : 5000;
    },
  });
  const v2Enabled = useFlag(STOCK_FOOTAGE_MATCHING_NEW_FLAG);
  const videoStatus = video?.status;
  const isEditable = isDraftVideoEditable(videoStatus);
  const liveDraftConfig = video?.draftConfig;
  const liveEditorEnabled =
    v2Enabled &&
    (video?.schemaVersion === 2 ||
      AUTHORED_TEMPLATES.some((t) => t.id === video?.variationId));
  const { prepareVideoPreviewAsync, isPreparing } = usePrepareVideoPreview();
  const openEditor = async () => {
    if (!liveEditorEnabled) {
      setPickerOpen(true);
      return;
    }
    try {
      await prepareVideoPreviewAsync(activeVideoId);
      setEditOpen(true);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not open the video editor'
      );
    }
  };
  useEffect(() => {
    if (liveDraftConfig?.textFrames)
      setEditableFrames(liveDraftConfig.textFrames);
  }, [liveDraftConfig?.textFrames]);
  const liveSelectedIds = useMemo(
    () =>
      liveDraftConfig ? getOrderedDraftClipAssetIds(liveDraftConfig) : null,
    [liveDraftConfig]
  );

  useEffect(() => {
    if (!liveSelectedIds) return;
    setSelectedIds(liveSelectedIds);
    if (videoStatus === 'queued' || videoStatus === 'processing')
      setPickerOpen(false);
  }, [liveSelectedIds, videoStatus]);

  // We need clip metadata (name, thumbnail, duration) to render the strip.
  // Fetching the full org video library is the same call the picker dialog
  // makes, so the query is shared via React Query — no duplicate roundtrip.
  const { assets } = useListAssets({
    type: 'video',
    source: 'raw',
    limit: 100,
  });
  const selectedAssets = useSelectedAssetMetadata(selectedIds, assets);
  const assetById = new Map(selectedAssets.map((asset) => [asset.id, asset]));

  // The write lives in its operation's mutation hook, which owns the payload
  // builder and the cache invalidation (form-contract source rule).
  const { patchDraftConfigAsync: patchClips, isPatching: patching } =
    usePatchVideoDraftConfig();

  const { queueExportAsync, isQueuing } = useQueueVideoExport({
    onSuccess: (exported) =>
      setQueuedVideo({ videoId: exported.id, status: exported.status }),
  });

  const followSavedVideo = (result: PatchVideoDraftConfigResponse) => {
    const nextId = result.video.id;
    if (nextId !== activeVideoId) setActiveVideoId(nextId);
    onSaved?.(result);
  };

  const persistSelection = async (next: string[]) => {
    const previous = selectedIds;
    setSelectedIds(next);
    try {
      const result = await patchClips({
        videoId: activeVideoId,
        clipAssetIds: next,
      });
      // Editing a video that already rendered forks rather than overwrites, so
      // follow the fork. `forkedFromVideoId` is only set when that happened.
      followSavedVideo(result);
    } catch (error) {
      setSelectedIds(previous);
      toast.error(
        error instanceof Error ? error.message : 'Failed to update clips'
      );
    }
  };

  const handleRemove = (assetId: string) => {
    void persistSelection(selectedIds.filter((id) => id !== assetId));
  };

  const handlePickerConfirm = (ids: string[]) => {
    void persistSelection(ids);
  };

  const persistTextFrames = async (next: EditableTextFrame[]) => {
    const previous = editableFrames;
    setEditableFrames(next);
    try {
      const result = await patchClips({
        videoId: activeVideoId,
        patch: { textFrames: next },
      });
      followSavedVideo(result);
    } catch (error) {
      setEditableFrames(previous);
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to update on-screen text'
      );
    }
  };

  const handleEditorChange = (change: DraftEditorChange | null) => {
    pendingChangeRef.current = change;
    setPendingChange(change);
  };

  /** Saves the editor's unsaved change. Resolves `true` when there was nothing
   * to save or the save landed. */
  const persistPendingChange = async (): Promise<boolean> => {
    const pending = pendingChangeRef.current;
    if (!pending) return true;
    try {
      const result = await patchClips({
        videoId: activeVideoId,
        patch: {
          ...(pending.bRollClips ? { bRollClips: pending.bRollClips } : {}),
          ...(pending.clipTiming ? { clipTiming: pending.clipTiming } : {}),
          ...(pending.musicVolume !== undefined
            ? { musicVolume: pending.musicVolume }
            : {}),
          ...(pending.musicTrackId
            ? { musicTrackId: pending.musicTrackId }
            : {}),
          ...(pending.scriptRoles ? { scriptRoles: pending.scriptRoles } : {}),
          ...(pending.overlayLayout
            ? { overlayLayout: pending.overlayLayout }
            : {}),
          ...(pending.scriptContent
            ? { scriptContent: pending.scriptContent }
            : {}),
        },
      });
      handleEditorChange(null);
      followSavedVideo(result);
      return true;
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to save your changes'
      );
      return false;
    }
  };

  const handleSave = async () => {
    if (await persistPendingChange()) {
      toast.success('Changes saved');
      setEditOpen(false);
    }
  };

  const handleDiscard = () => {
    handleEditorChange(null);
    // Remount the editor so the next open starts from the saved draft rather
    // than the edits that were just thrown away.
    setEditorKey((k) => k + 1);
    setEditOpen(false);
  };

  const belowMin = selectedIds.length < minClipCount;

  const [musicOpen, setMusicOpen] = useState(false);
  const savedMusicTrackId = liveDraftConfig?.musicTrackId ?? null;
  const savedMusicVolume = liveDraftConfig?.musicVolume ?? 0;
  const savedIsUpload = savedMusicTrackId === UPLOADED_TRACK_ID;
  const { uploads: musicUploads } = useListMusicUploads(savedIsUpload);
  const savedUploadName = savedIsUpload
    ? musicUploads.find(
        (upload) => upload.blobUrl === liveDraftConfig?.musicUrl
      )?.name
    : undefined;
  const musicLabel =
    savedMusicTrackId && savedMusicVolume > 0
      ? `${savedUploadName ?? prettyTrackName(savedMusicTrackId)} · ${Math.round(savedMusicVolume * 100)}%`
      : 'No music';

  const saveMusic = async (choice: {
    trackId: string | null;
    url: string | null;
    volume: number;
    startSec?: number;
  }) => {
    try {
      const result = await patchClips({
        videoId: activeVideoId,
        patch: choice.trackId
          ? {
              musicTrackId: choice.trackId,
              ...(choice.url ? { musicUrl: choice.url } : {}),
              musicVolume: choice.volume,
              ...(choice.startSec !== undefined
                ? { musicStartSec: choice.startSec }
                : {}),
            }
          : { musicVolume: 0 },
      });
      followSavedVideo(result);
      setMusicOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error ? error.message : 'Failed to update music'
      );
    }
  };

  const sentenceNode = (
    <>
      Create <span className="font-medium">&quot;{title}&quot;</span> using{' '}
      <span className="font-medium">
        {selectedIds.length} clip{selectedIds.length === 1 ? '' : 's'}
      </span>{' '}
      selected for this video?
    </>
  );

  const busy = isQueuing || startingExport || patching || isPreparing;
  const handleReject = () => {
    if (busy || exportStarted.current) return;
    setDecision('rejected');
    onSendMessage?.("Don't render this video.");
  };
  const handleAccept = async () => {
    if (busy || exportStarted.current || !isEditable || belowMin) return;
    exportStarted.current = true;
    setStartingExport(true);
    try {
      await queueExportAsync({ videoId: activeVideoId });
    } catch {
      // The mutation reports the error; keep this proposal available to retry.
      exportStarted.current = false;
    } finally {
      setStartingExport(false);
    }
  };

  const renderedVideo =
    queuedVideo ??
    (videoStatus === 'ready' ||
    videoStatus === 'queued' ||
    videoStatus === 'processing'
      ? { videoId: activeVideoId, status: videoStatus }
      : null);

  const composerGate =
    decision === 'pending' && !renderedVideo
      ? {
          sentence: sentenceNode,
          allowMessage: belowMin,
          onAccept: handleAccept,
          onReject: handleReject,
          acceptLabel: isQueuing ? 'Starting…' : 'Create video',
          onEdit: isEditable ? openEditor : undefined,
          busy,
          acceptDisabled: !isEditable || belowMin,
          acceptDisabledReason: !isEditable
            ? 'Wait for the draft to finish loading.'
            : belowMin
              ? `Choose at least ${minClipCount} clip${minClipCount === 1 ? '' : 's'} first.`
              : undefined,
        }
      : null;

  const editorDialogs = (
    <>
      {isEditable && (
        <ClipPickerDialog
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          selectedIds={selectedIds}
          onConfirm={handlePickerConfirm}
          serviceId={serviceId ?? video?.serviceId ?? null}
          minCount={minClipCount}
        />
      )}
      {isEditable && editOpen && (
        <Suspense fallback={null}>
          <VideoDraftEditDialog
            open={editOpen}
            onOpenChange={setEditOpen}
            videoId={activeVideoId}
            editorKey={editorKey}
            storedClips={liveDraftConfig?.bRollClips ?? []}
            storedMusicTrackId={liveDraftConfig?.musicTrackId}
            storedMusicVolume={liveDraftConfig?.musicVolume}
            storedCopy={draftCopyOf(liveDraftConfig)}
            storedOverlayLayout={liveDraftConfig?.overlayLayout}
            storedClipTiming={liveDraftConfig?.clipTiming}
            clipInfo={(assetId) => {
              const asset = assetById.get(assetId);
              return {
                name: asset?.name ?? undefined,
                sourceDurationSec: asset?.duration ?? null,
                thumbnailUrl: asset?.thumbnailUrl ?? null,
              };
            }}
            // The picker PATCHes immediately and its save refetches the preview,
            // so unsaved edits are saved FIRST — otherwise the new compile would
            // throw them away.
            onAddClips={() => {
              void persistPendingChange().then(
                (saved) => saved && setPickerOpen(true)
              );
            }}
            onChange={handleEditorChange}
            hasUnsavedChanges={pendingChange !== null}
            onSave={() => void handleSave()}
            onDiscard={handleDiscard}
            saving={patching}
            textFrames={editableFrames}
            onTextFramesChange={(next) => void persistTextFrames(next)}
          />
        </Suspense>
      )}
    </>
  );

  if (editorOnly) {
    if (!liveEditorEnabled) return null;
    return (
      <>
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label="Edit video"
          disabled={!isEditable || busy}
          onClick={() => void openEditor()}
        >
          <Pencil className="size-3.5" /> Edit
        </Button>
        {editorDialogs}
      </>
    );
  }

  // Once export is queued, hand off to the same polling card
  // `content_renderVideo`'s own output already mounts, so the transcript
  // reads identically regardless of which path queued the render.

  if (renderedVideo) {
    return <ProcessingStatus data={renderedVideo} onPrompt={onSendMessage} />;
  }

  // Keep status visible for old renders, but do not offer creation with a retired layout.
  if (v2Enabled && video && video.schemaVersion !== 2) return null;

  if (decision === 'rejected') {
    return (
      <div
        data-testid="claire-card-video-draft"
        className="w-full rounded-lg border bg-card p-3 text-xs text-muted-foreground sm:max-w-lg"
      >
        Discarded. Nothing was rendered.
      </div>
    );
  }

  return (
    <ComposerDecision id={id} gate={composerGate}>
      <div
        data-testid="claire-card-video-draft"
        className="w-full rounded-lg border bg-card p-4 sm:max-w-lg"
      >
        <div className="flex items-start gap-2 text-xs">
          <Sparkles className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          <p>{sentenceNode}</p>
        </div>

        {fields.length > 0 && (
          <dl className="mt-3 space-y-1.5 pl-5.5">
            {fields
              // Hide the auto-generated "Clips" summary field — we render the
              // interactive strip below instead, and showing both is noise.
              // Hide the "Script" field too when we have textFrames — the
              // text-frame preview shows the same content with proper styling
              // per frame, so the raw script repeat is duplicative.
              .filter(
                (f) =>
                  f.label !== 'Clips' &&
                  !(f.label === 'Script' && textFrames && textFrames.length > 0)
              )
              .map((field) => (
                <div
                  key={field.label}
                  className="flex items-baseline justify-between gap-3 text-xs"
                >
                  <dt className="text-muted-foreground shrink-0">
                    {field.label}
                  </dt>
                  <dd className="truncate font-medium text-right">
                    {field.label === 'Status' && video
                      ? videoStatusLabels[video.status]
                      : field.value}
                  </dd>
                </div>
              ))}
          </dl>
        )}

        {textFrames && textFrames.length > 0 && (
          <div className="mt-4 space-y-2 pl-5.5">
            <div className="text-xs text-muted-foreground">
              On-screen text ({textFrames.length} frame
              {textFrames.length === 1 ? '' : 's'})
            </div>
            <ol className="space-y-1.5">
              {textFrames.map((frame, idx) => {
                const badge = STYLE_BADGES[frame.style ?? 'default'];
                return (
                  <li
                    key={frame.id}
                    className="rounded-md border bg-card/50 p-2"
                  >
                    <div className="flex items-center gap-1.5">
                      <span className="text-[10px] text-muted-foreground/70 tabular-nums">
                        {idx + 1}
                      </span>
                      <span
                        className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${badge.className}`}
                      >
                        {badge.label}
                      </span>
                    </div>
                    <p className="mt-1 text-xs leading-snug break-words">
                      {frame.text}
                    </p>
                  </li>
                );
              })}
            </ol>
          </div>
        )}

        <div className="mt-4 flex items-center justify-between gap-3 pl-5.5 text-xs">
          <span className="flex min-w-0 items-center gap-1.5">
            <Music className="size-3.5 shrink-0 text-muted-foreground" />
            <span className="text-muted-foreground">Music</span>
            <span className="truncate font-medium">{musicLabel}</span>
          </span>
          {isEditable ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 shrink-0 gap-1 text-xs"
              onClick={() => setMusicOpen(true)}
              disabled={patching}
            >
              <Pencil className="size-3" />
              Edit music
            </Button>
          ) : null}
        </div>

        <div className="mt-4 space-y-2 pl-5.5">
          {belowMin && (
            <p className="text-[11px] text-amber-600 dark:text-amber-400">
              Pick at least {minClipCount} clip{minClipCount === 1 ? '' : 's'}{' '}
              before rendering.
            </p>
          )}

          {selectedIds.length === 0 ? (
            <p className="rounded-md border border-dashed px-3 py-4 text-center text-xs text-muted-foreground">
              No clips selected yet.
            </p>
          ) : (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {selectedIds.map((assetId) => {
                const asset = assetById.get(assetId);
                return (
                  <div
                    key={assetId}
                    className="relative shrink-0 overflow-hidden rounded-md border bg-muted"
                    style={{ width: '88px', aspectRatio: '16 / 9' }}
                  >
                    {asset?.thumbnailUrl ? (
                      <img
                        src={asset.thumbnailUrl}
                        alt=""
                        className="size-full object-cover"
                      />
                    ) : asset?.blobUrl ? (
                      <VideoThumbnail
                        src={asset.blobUrl}
                        className="size-full"
                        isImage={asset.type === 'image'}
                      />
                    ) : (
                      <div className="flex size-full items-center justify-center">
                        <VideoIcon className="size-4 text-muted-foreground/40" />
                      </div>
                    )}
                    {isEditable && (
                      <button
                        type="button"
                        aria-label="Remove clip"
                        onClick={() => handleRemove(assetId)}
                        disabled={busy}
                        className="absolute right-1 top-1 rounded-full bg-black/70 p-0.5 text-white hover:bg-black/90 disabled:opacity-50"
                      >
                        <X className="size-3" />
                      </button>
                    )}
                    {asset?.name && (
                      <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/80 to-transparent px-1.5 py-0.5 text-[9px] text-white">
                        {asset.name}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>

        <div className="mt-4">
          <DecisionFooter
            hideSentence
            sentence={sentenceNode}
            onAccept={handleAccept}
            onReject={handleReject}
            acceptLabel={isQueuing ? 'Starting…' : 'Create video'}
            onEdit={isEditable ? openEditor : undefined}
            busy={busy}
            acceptDisabled={!isEditable || belowMin}
            acceptDisabledReason={
              !isEditable
                ? 'Wait for the draft to finish loading.'
                : belowMin
                  ? `Choose at least ${minClipCount} clip${minClipCount === 1 ? '' : 's'} first.`
                  : undefined
            }
          />
        </div>

        {isEditable && musicOpen ? (
          <CardMusicEditor
            open={musicOpen}
            onOpenChange={setMusicOpen}
            value={{
              trackId: savedMusicVolume > 0 ? savedMusicTrackId : null,
              url: liveDraftConfig?.musicUrl ?? null,
              volume: savedMusicVolume > 0 ? savedMusicVolume : 0.5,
            }}
            onSave={saveMusic}
            saving={patching}
          />
        ) : null}

        {editorDialogs}
      </div>
    </ComposerDecision>
  );
}

/** Open the same visual editor from a draft or finished video's panel. */
export function VideoEditorButton({
  videoId,
  onSaved,
}: {
  videoId: string;
  onSaved: (result: PatchVideoDraftConfigResponse) => void;
}) {
  const v2Enabled = useFlag(STOCK_FOOTAGE_MATCHING_NEW_FLAG);
  if (!v2Enabled) return null;
  return (
    <VideoDraftPreviewCard
      key={videoId}
      id={`editor-${videoId}`}
      videoId={videoId}
      title="Video"
      fields={[]}
      initialClipAssetIds={[]}
      editorOnly
      onSaved={onSaved}
    />
  );
}

function prettyTrackName(trackId: string) {
  if (trackId === UPLOADED_TRACK_ID) return 'Your upload';
  return trackId
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Mounted only while open, so the track library loads on demand. */
function CardMusicEditor(
  props: Omit<
    React.ComponentProps<typeof MusicEditorDialog>,
    'tracks' | 'isLoading'
  >
) {
  const { tracks, isLoading } = useListMusicTracks(props.open);
  const { uploads, isLoading: uploadsLoading } = useListMusicUploads(
    props.open
  );
  return (
    <MusicEditorDialog
      {...props}
      tracks={[...uploads.map(musicUploadAsTrack), ...tracks]}
      isLoading={isLoading || uploadsLoading}
    />
  );
}
